/** Result captions are labels; numeric values and instructions stay gameplay. */
export function qaTextRole(text: string): "label" | "gameplay" {
  return /^(DAMAGE|HITS|BLOCKS|[12] WINS? TO OPEN)$/.test(text)
    ? "label"
    : "gameplay";
}

// QA-only (?qa=1): records every canvas fillText run with its on-screen
// size in CSS pt and its screen-space bbox, one frame at a time.
export interface TextRun {
  id: string;
  text: string;
  color: string;
  strokeColor?: string;
  strokeWidth?: number;
  pt: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

const canvases = new Map<HTMLCanvasElement, TextRun[]>();
let last: TextRun[] = [];
let installed = false;

export function installTextRecorder(): void {
  if (installed || typeof CanvasRenderingContext2D === "undefined") return;
  installed = true;
  // A cached HUD canvas keeps its text until its next clear/redraw.
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const clear = CanvasRenderingContext2D.prototype.clearRect;
  CanvasRenderingContext2D.prototype.clearRect = function (
    this: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  ) {
    canvases.set(this.canvas, []);
    clear.call(this, x, y, w, h);
  };
  // Track strokes actually drawn, rather than the context's stale strokeStyle.
  const strokes = new WeakMap<
    CanvasRenderingContext2D,
    { text: string; x: number; y: number; color: string; width: number }
  >();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const stroke = CanvasRenderingContext2D.prototype.strokeText;
  CanvasRenderingContext2D.prototype.strokeText = function (
    text,
    x,
    y,
    maxWidth,
  ) {
    strokes.set(this, {
      text,
      x,
      y,
      color:
        typeof this.strokeStyle === "string"
          ? this.strokeStyle.toUpperCase()
          : "gradient",
      width: this.lineWidth,
    });
    if (maxWidth === undefined) stroke.call(this, text, x, y);
    else stroke.call(this, text, x, y, maxWidth);
  };
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const original = CanvasRenderingContext2D.prototype.fillText;
  CanvasRenderingContext2D.prototype.fillText = function (
    this: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth?: number,
  ) {
    try {
      const outline = strokes.get(this);
      record(this, text, x, y);
      if (outline?.text === text && outline.x === x && outline.y === y) {
        const run = canvases.get(this.canvas)?.at(-1);
        if (run) {
          const m = this.getTransform();
          const cssScale = window.innerHeight / this.canvas.height;
          run.strokeColor = outline.color;
          run.strokeWidth = outline.width * Math.abs(m.d) * cssScale;
        }
      }
      strokes.delete(this);
    } catch {
      // never break rendering for QA bookkeeping
    }
    if (maxWidth === undefined) original.call(this, text, x, y);
    else original.call(this, text, x, y, maxWidth);
  };
}

function record(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
): void {
  if (ctx.globalAlpha <= 0.01 || ctx.canvas.dataset?.qaIgnoreText === "true")
    return;
  const canvas = ctx.canvas;
  const rect = canvas.isConnected
    ? canvas.getBoundingClientRect()
    : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  const sx = rect.width / canvas.width;
  const sy = rect.height / canvas.height;
  const m = ctx.getTransform();
  const fontPx = Number(/(\d+(?:\.\d+)?)px/.exec(ctx.font)?.[1] ?? "10");
  const metrics = ctx.measureText(text);
  const left = x - metrics.actualBoundingBoxLeft;
  const top = y - metrics.actualBoundingBoxAscent;
  const w = metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight;
  const h = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  const current = canvases.get(canvas) ?? [];
  canvases.set(canvas, current);
  current.push({
    id: `text:${text}`,
    text,
    color:
      typeof ctx.fillStyle === "string"
        ? ctx.fillStyle.toUpperCase()
        : "gradient",
    pt: fontPx * m.d * sy,
    x: rect.left + (m.a * left + m.e) * sx,
    y: rect.top + (m.d * top + m.f) * sy,
    w: m.a * w * sx,
    h: m.d * h * sy,
  });
}

export function beginTextFrame(): void {
  // Retain runs on canvases that did not need a redraw.
}

export function endTextFrame(): void {
  last = [...canvases.values()].flat();
}

export function lastTextRuns(): TextRun[] {
  return last;
}
